import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useEffectOnce } from "usehooks-ts";
import { Button, Input, theme } from "antd";
import {
  FolderOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Flex } from "src/components/Flex";
import { usePopupContext } from "src/hooks/usePopup";
import {
  DeepLinkSpaceItem,
  EntityLinkSpaceItem,
  SpaceItemType,
} from "pages/api/[orgId]/spaces/types";
import { useSpacesQuery } from "./useSpacesQuery";
import { Background } from "src/components/patterns/Background";

type SpaceItemProto = {
  type: SpaceItemType;
  name: string;
  description?: string;
} & (
  | {
      type: SpaceItemType.Board | SpaceItemType.Query;
      config: DeepLinkSpaceItem["config"];
    }
  | {
      type: SpaceItemType.Entity | SpaceItemType.Space;
      config: EntityLinkSpaceItem["config"];
    }
);

export function AddToSpacePicker({
  children,
  item,
  onClose,
  ...props
}: PropsWithChildren<{
  item: SpaceItemProto;
  target?: HTMLElement;
  onClose?: () => void;
  open?: boolean;
}>) {
  const [open, setOpen] = useState(props.open);
  const [target, setTarget] = useState(props.target);
  return (
    <>
      {props.open ? null : (
        <div
          onClick={(e) => {
            setTarget(e.target as HTMLElement);
            setOpen(true);
          }}
        >
          {children}
        </div>
      )}
      {open && target && (
        <PopupLoader
          onClose={() => {
            setOpen(false);
            setTarget(undefined);
            onClose && onClose();
          }}
          target={target}
        >
          <SpacePicker item={item} />
        </PopupLoader>
      )}
    </>
  );
}

function SpacePicker({ item }: { item: SpaceItemProto }) {
  const { token } = theme.useToken();
  const { resize, close } = usePopupContext();
  const query = useSpacesQuery();
  const [filter, setFilter] = useState("");
  const terms = useMemo(
    () =>
      filter
        .split(" ")
        .map((str) => str.toLowerCase().trim())
        .filter(Boolean) as string[],
    [filter]
  );
  const spaces = (query.spacesQuery.data || []).filter((space) =>
    terms.every((term) => space.name.toLowerCase().includes(term))
  );
  const [addingToSpace, setAddingTo] = useState("");

  useEffect(() => resize(), [spaces.length, resize, filter, addingToSpace]);
  return addingToSpace ? (
    <div>
      <MiniSpaceItemForm
        loading={query.createSpaceItem.isLoading}
        name={item.name}
        description={item.description}
        onChange={(fields) => {
          query.createSpaceItem
            .mutateAsync({
              space_id: addingToSpace,
              type: item.type,
              name: fields.name,
              description: fields.description,
              ...item.config,
            })
            .finally(() => close());
        }}
      />
    </div>
  ) : (
    <div>
      <div style={{ padding: 4 }}>
        <Input
          allowClear
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Find or create a space..."
          addonBefore={<FolderOutlined />}
        />
      </div>
      <div style={{ maxHeight: 400, overflow: "auto", padding: 4 }}>
        {((filter && spaces.length === 0) || !query.spacesQuery.isFetched) && (
          <Flex
            style={{ padding: 8, fontSize: 12, color: token.colorTextTertiary }}
          >
            {query.upsertSpace.isLoading || !query.spacesQuery.isFetched ? (
              <LoadingOutlined />
            ) : (
              "no matches"
            )}
          </Flex>
        )}
        {spaces.map((space) => (
          <div
            key={space.uuid}
            style={{ margin: "4px 0" }}
            onClick={() => setAddingTo(space.uuid)}
          >
            <Flex
              gap={8}
              className="item-link"
              style={{ padding: 4, minHeight: 32 }}
            >
              <Background
                width={32}
                height={32}
                pattern={space.pattern}
                colorName={space.color}
                style={{ borderRadius: 2 }}
              />
              <div
                style={{
                  flex: 1,
                  maxWidth: 300,
                  color: token.colorText,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                <div className="trunc">{space.name}</div>
              </div>
              {/**
              <div style={{ minWidth: 116 }}>
                <Button
                  type="default"
                  size="small"
                  onClick={() => {
                    query.createSpaceItem.mutate({
                      space_id: space.uuid,
                      type: item.type,
                      name: item.name,
                      ...item.config,
                    });
                    close();
                  }}
                >
                  <PlusOutlined />
                  Add to space
                </Button>
              </div>
              */}
            </Flex>
          </div>
        ))}
      </div>
      {filter.trim() && (
        <div
          style={{
            padding: "10px 6px",
            marginTop: 8,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            border: `solid 2px ${token.colorBgContainer}`,
            borderTopColor: token.colorBgLayout,
          }}
        >
          <Flex gap={8}>
            <Button
              disabled={!Boolean(filter.trim())}
              loading={query.upsertSpace.isLoading}
              type="primary"
              style={{
                fontSize: 13,
                width: 32,
                height: 32,
                padding: 0,
              }}
              onClick={() => {
                query.upsertSpace.mutate({
                  name: filter.trim(),
                  scope: "private",
                });
              }}
            >
              <PlusOutlined />
            </Button>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  color: token.colorTextTertiary,
                  lineHeight: 0.8,
                }}
              >
                Create a new space
              </div>
              <span
                className="trunc"
                style={{
                  flex: 1,
                  maxWidth: 300,
                  color: token.colorText,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {filter.trim()}
              </span>
            </div>
          </Flex>
        </div>
      )}
    </div>
  );
}

function MiniSpaceItemForm(props: {
  name?: string;
  description?: string;
  scope?: "global" | "org" | "private";
  loading?: boolean;
  onChange(item: {
    name: string;
    description: string;
    scope: "global" | "org" | "private";
  }): void;
}) {
  const [name, setName] = useState(props.name || "");
  const [description, setDescription] = useState(props.description || "");
  const [scope, _setScope] = useState(props.scope || "private");
  return (
    <div style={{ padding: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 500, margin: 8, marginTop: 16 }}>
        Item name
      </div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        status={name.trim() ? undefined : "error"}
      />
      <div style={{ fontSize: 14, fontWeight: 500, margin: 8, marginTop: 16 }}>
        Description
      </div>
      <Input.TextArea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        style={{ marginBottom: 16 }}
      />
      <Button
        type="primary"
        block
        disabled={!Boolean(name)}
        loading={props.loading}
        onClick={() =>
          name &&
          props.onChange({
            name,
            description,
            scope,
          })
        }
      >
        Save item to space
      </Button>
    </div>
  );
}

function PopupLoader({
  target,
  children,
  onClose,
}: PropsWithChildren<{
  target: HTMLElement;
  onClose?: () => void;
}>) {
  const { setPopup, close, isOpen, resize } = usePopupContext();
  const [ready, setReady] = useState(false);
  useEffect(() => () => close(), [close]);
  useEffectOnce(() => {
    setPopup(target, <>{children}</>);
    setReady(true);
  });
  useEffect(() => {
    if (ready && onClose && !isOpen) onClose();
  }, [isOpen, ready, onClose]);
  useEffect(() => {
    if (ready) resize();
  }, [ready, resize]);
  return null;
}
